/**
 * Marker Colors Configuration
 * Paleta de colores inspirada en Mapbox para marcadores de áreas recreativas
 * y diferentes tipos de espacios en proyectos inmobiliarios
 */

// Paleta base inspirada en Mapbox
export const MAPBOX_COLORS = {
  // Verdes - Naturaleza y áreas verdes
  green: {
    park: '#34C759',        // Verde brillante para parques
    forest: '#30D158',      // Verde claro para bosques
    garden: '#32D74B',      // Verde jardín
    field: '#20C997',       // Verde campo
    nature: '#10B981',      // Verde natural
  },

  // Azules - Agua y recreación
  blue: {
    pool: '#5AC8FA',        // Azul piscina
    water: '#0A84FF',       // Azul agua
    sports: '#3B82F6',      // Azul deportivo
    playground: '#60A5FA',  // Azul juegos
    sky: '#64B5F6',         // Azul cielo
  },

  // Naranjas/Amarillos - Amenidades y servicios
  orange: {
    amenity: '#FF9F0A',     // Naranja amenidad
    service: '#F59E0B',     // Naranja servicio
    court: '#FB8C00',       // Naranja cancha
    gym: '#FF9500',         // Naranja gimnasio
  },

  // Púrpuras - Puntos de interés especiales
  purple: {
    landmark: '#BF5AF2',    // Púrpura landmark
    special: '#8B5CF6',     // Púrpura especial
    viewpoint: '#A855F7',   // Púrpura mirador
    cultural: '#9333EA',    // Púrpura cultural
  },

  // Rosas/Magentas - Entretenimiento y eventos
  pink: {
    event: '#FF2D55',       // Rosa evento
    club: '#EC4899',        // Rosa club
    party: '#F472B6',       // Rosa fiesta
    social: '#FF45A4',      // Rosa social
  },

  // Rojos - Servicios críticos
  red: {
    emergency: '#FF3B30',   // Rojo emergencia
    exit: '#EF4444',        // Rojo salida
    fire: '#DC2626',        // Rojo fuego
    alert: '#F43F5E',       // Rojo alerta
  },

  // Grises - Estructuras e infraestructura
  gray: {
    building: '#8E8E93',    // Gris edificio
    parking: '#6B7280',     // Gris estacionamiento
    road: '#9CA3AF',        // Gris camino
    structure: '#A3A3A3',   // Gris estructura
  },

  // Marrones - Tierra y natural
  brown: {
    trail: '#A68A64',       // Marrón sendero
    earth: '#92672E',       // Marrón tierra
    wood: '#8B7355',        // Marrón madera
  },

  // Cyan/Teal - Instalaciones modernas
  teal: {
    modern: '#5AC8FA',      // Cyan moderno
    tech: '#06B6D4',        // Teal tecnología
    facility: '#14B8A6',    // Teal facilidad
    wellness: '#2DD4BF',    // Teal bienestar
  }
};

/**
 * Configuración de colores para áreas recreativas
 * Cada tipo tiene un color principal y variantes
 */
export const RECREATIONAL_AREA_COLORS = {
  // Áreas verdes y naturaleza
  'Parque': {
    primary: MAPBOX_COLORS.green.park,
    icon: '🌳',
    category: 'naturaleza'
  },
  'Área Verde': {
    primary: MAPBOX_COLORS.green.nature,
    icon: '🌿',
    category: 'naturaleza'
  },
  'Plaza': {
    primary: MAPBOX_COLORS.green.garden,
    icon: '🏛️',
    category: 'naturaleza'
  },
  'Jardín': {
    primary: MAPBOX_COLORS.green.garden,
    icon: '🌺',
    category: 'naturaleza'
  },

  // Deportes y actividad física
  'Cancha Deportiva': {
    primary: MAPBOX_COLORS.orange.court,
    icon: '⚽',
    category: 'deportes'
  },
  'Cancha de Fútbol': {
    primary: MAPBOX_COLORS.green.field,
    icon: '⚽',
    category: 'deportes'
  },
  'Cancha de Básquet': {
    primary: MAPBOX_COLORS.orange.court,
    icon: '🏀',
    category: 'deportes'
  },
  'Cancha de Tenis': {
    primary: MAPBOX_COLORS.blue.sports,
    icon: '🎾',
    category: 'deportes'
  },
  'Cancha de Vóley': {
    primary: MAPBOX_COLORS.blue.pool,
    icon: '🏐',
    category: 'deportes'
  },
  'Gimnasio': {
    primary: MAPBOX_COLORS.orange.gym,
    icon: '💪',
    category: 'deportes'
  },

  // Agua y recreación acuática
  'Piscina': {
    primary: MAPBOX_COLORS.blue.pool,
    icon: '🏊',
    category: 'agua'
  },
  'Zona de Juegos': {
    primary: MAPBOX_COLORS.blue.playground,
    icon: '🎪',
    category: 'recreacion'
  },

  // Instalaciones sociales y eventos
  'Área de Picnic': {
    primary: MAPBOX_COLORS.green.park,
    icon: '🧺',
    category: 'social'
  },
  'Parque Infantil': {
    primary: MAPBOX_COLORS.pink.social,
    icon: '🎠',
    category: 'recreacion'
  },
  'Club House': {
    primary: MAPBOX_COLORS.pink.club,
    icon: '🏠',
    category: 'social'
  },
  'Salón de Eventos': {
    primary: MAPBOX_COLORS.pink.event,
    icon: '🎉',
    category: 'social'
  },

  // Servicios y facilidades
  'Estacionamiento': {
    primary: MAPBOX_COLORS.gray.parking,
    icon: '🅿️',
    category: 'servicios'
  },
  'Quincho': {
    primary: MAPBOX_COLORS.orange.amenity,
    icon: '🔥',
    category: 'social'
  },

  // Miradores y puntos especiales
  'Mirador': {
    primary: MAPBOX_COLORS.purple.viewpoint,
    icon: '🔭',
    category: 'especial'
  },

  // Otros - tipo genérico con color diferenciado
  'Otro': {
    primary: MAPBOX_COLORS.teal.facility,
    icon: '📍',
    category: 'otro'
  }
};

/**
 * Obtiene el color para un tipo de área recreativa
 * Si no existe el tipo, retorna un color por defecto
 */
export const getRecreationalAreaColor = (type) => {
  const config = RECREATIONAL_AREA_COLORS[type];
  if (config) {
    return config.primary;
  }
  // Color por defecto si no se encuentra el tipo
  return MAPBOX_COLORS.teal.facility;
};

/**
 * Obtiene el icono para un tipo de área recreativa
 */
export const getRecreationalAreaIcon = (type) => {
  const config = RECREATIONAL_AREA_COLORS[type];
  return config ? config.icon : '📍';
};

/**
 * Configuración de colores para otros tipos de marcadores
 */
export const MARKER_TYPE_COLORS = {
  entrada: MAPBOX_COLORS.green.nature,
  salida: MAPBOX_COLORS.red.exit,
  etapa: MAPBOX_COLORS.orange.service,
  bloque: '#FFFFFF',  // Bloque mantiene blanco para contraste
  arboles: MAPBOX_COLORS.green.forest,
  estacionamiento: MAPBOX_COLORS.blue.sports,
  punto_interes: MAPBOX_COLORS.purple.landmark,
  punto_foto: MAPBOX_COLORS.pink.club,
  default: MAPBOX_COLORS.teal.facility
};

/**
 * Obtiene el color para un tipo de marcador específico
 */
export const getMarkerColor = (type) => {
  return MARKER_TYPE_COLORS[type] || MARKER_TYPE_COLORS.default;
};

/**
 * Paleta de colores de respaldo para marcadores personalizados
 * Array de colores diversos para asignación automática
 */
export const DIVERSE_MARKER_PALETTE = [
  MAPBOX_COLORS.green.park,
  MAPBOX_COLORS.blue.pool,
  MAPBOX_COLORS.orange.amenity,
  MAPBOX_COLORS.purple.landmark,
  MAPBOX_COLORS.pink.event,
  MAPBOX_COLORS.teal.modern,
  MAPBOX_COLORS.green.forest,
  MAPBOX_COLORS.blue.sports,
  MAPBOX_COLORS.orange.court,
  MAPBOX_COLORS.purple.special,
  MAPBOX_COLORS.pink.social,
  MAPBOX_COLORS.teal.facility,
];

/**
 * Obtiene un color de la paleta diversa según un índice
 * Útil para asignar colores automáticamente a múltiples marcadores
 */
export const getColorFromPalette = (index) => {
  return DIVERSE_MARKER_PALETTE[index % DIVERSE_MARKER_PALETTE.length];
};

export default {
  MAPBOX_COLORS,
  RECREATIONAL_AREA_COLORS,
  MARKER_TYPE_COLORS,
  getRecreationalAreaColor,
  getRecreationalAreaIcon,
  getMarkerColor,
  getColorFromPalette,
  DIVERSE_MARKER_PALETTE
};
